import type { DbHealthReport } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { DetailList } from '#client/components/ui/DetailList';
import { blockedSummary, databaseDetails } from './database-health.logic';
import {
  checkColumns,
  connectionColumns,
  indexColumns,
  latencyColumns,
  statementColumns,
  tableColumns,
} from './health-columns';

interface SectionProps {
  report: DbHealthReport;
}

/** The alerts, the database's own figures, and anything stuck on a lock. */
export function HealthOverview({ report }: SectionProps) {
  return (
    <>
      <Card title="Checks" flush>
        <DataTable
          columns={checkColumns}
          rows={report.checks}
          rowKey={(row) => row.key}
          caption="Health checks"
        />
      </Card>

      <Card title="Database">
        <DetailList items={databaseDetails(report.database)} />
      </Card>

      {report.blocked.length > 0 && (
        <Banner tone="critical">{blockedSummary(report.blocked)}</Banner>
      )}
    </>
  );
}

/** What the database is being asked to do: requests, statements, connections. */
export function WorkloadCards({ report }: SectionProps) {
  return (
    <>
      <Card
        title="Request latency"
        subtitle="This server process since it started; each blue/green slot keeps its own"
        flush
      >
        <DataTable
          columns={latencyColumns}
          rows={report.latency}
          rowKey={(row) => row.route}
          caption="Request latency by route"
          emptyState="No requests recorded yet"
        />
      </Card>

      <Card title="Heaviest statements" subtitle="By total time, from pg_stat_statements" flush>
        {report.statements === null ? (
          <Banner tone="info">{report.statements_note}</Banner>
        ) : (
          <DataTable
            columns={statementColumns}
            rows={report.statements}
            rowKey={(row) => row.query}
            caption="Heaviest statements"
            emptyState="No statements recorded yet"
          />
        )}
      </Card>

      <Card title="Connections" subtitle="To this database, from every application" flush>
        <DataTable
          columns={connectionColumns}
          rows={report.connections}
          rowKey={(row) => `${row.application}:${row.state}`}
          caption="Connections by application and state"
        />
      </Card>
    </>
  );
}

/** Where the data lives: tables and their indexes. */
export function StorageCards({ report }: SectionProps) {
  return (
    <>
      <Card title="Tables" subtitle="Largest first" flush>
        <DataTable
          columns={tableColumns}
          rows={report.tables}
          rowKey={(row) => row.table}
          caption="Table statistics"
        />
      </Card>

      <Card title="Indexes" subtitle="Least used first" flush>
        <DataTable
          columns={indexColumns}
          rows={report.indexes}
          rowKey={(row) => row.index}
          caption="Index usage"
        />
      </Card>
    </>
  );
}
